import { computed, ref, watch, type Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { displayPagingRange } from "@/common/common";
import { getChainInfo } from "@/common/graphql/chain";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { GeneralConstant, DeliveryRequest } from "@/common/constant";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetGroup,
  localStorageGetPageHeaders,
  localStorageSetPageHeaders,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  DeliveryRequestCountInput,
  DeliveryRequestListInput,
  ListDeliveryRequestDocument,
  ListDeliveryRequestQuery,
  ListDeliveryRequestQueryVariables,
  OperationInput,
} from "@/generated/graphql";

interface TableHeader {
  id: number;
  display: boolean;
  title: string;
  settingText: string;
  align: string;
  sortable: boolean;
  orderDisabled: boolean;
  order: string;
  orderKey: string;
  key: string;
}

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliveryRequestTableParams {
  formatDateForDisplay: (date: Date | null) => string | null;
  searchConditionReceptionNo: Ref<string | undefined | null>;
  searchConditionMemberId: Ref<string | undefined | null>;
  searchConditionName: Ref<string | undefined | null>;
  searchConditionPhone: Ref<string | undefined | null>;
  searchConditionZipCode: Ref<string | undefined | null>;
  searchConditionRegisterNo: Ref<number | undefined | null>;
  searchConditionReceiptNo: Ref<number | undefined | null>;
  searchConditionReceptionDateFrom: Ref<Date | null>;
  searchConditionReceptionDateTo: Ref<Date | null>;
  searchConditionDeliveryDateFrom: Ref<Date | null>;
  searchConditionDeliveryDateTo: Ref<Date | null>;
  areaSelectValues: Ref<{ area_code: string; area_name: string } | null>;
  statusSelectedItem: Ref<string[] | number[]>;
  searchConditionChainCode: Ref<(string | null | undefined)[] | null | undefined>;
  searchConditionShopCode: Ref<(string | null | undefined)[] | null | undefined>;
  authType: Ref<string>;
  chainViewFlag: boolean;
  isDeliveryAddressDisplay: Ref<number>;
  isPersonalInfoPossible: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editPage: Ref<boolean>;
  displayListSearchDialog: Ref<boolean>;
}

const pickupDeliveryStatusName = (id: undefined | null | string): string => {
  for (const status of DeliveryRequest.status) {
    if (status.id === id) {
      return status.name;
    }
  }
  return "";
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestHeaders = () => {
  const defaultTableHeaders: TableHeader[] = [
    { id: 1, display: true, title: "受付番号", settingText: "受付番号", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "receptionNo" },
    { id: 15, display: false, title: "チェーン名", settingText: "チェーン名", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "chainName" },
    { id: 2, display: true, title: "店舗名", settingText: "店舗名", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "shopName" },
    { id: 3, display: false, title: "会員番号", settingText: "会員番号", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "memberId" },
    { id: 4, display: true, title: "名前(漢字)", settingText: "名前(漢字)", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "name" },
    { id: 5, display: true, title: "便名(配達時間帯)", settingText: "便名(配達時間帯)", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "deliveryIdName" },
    { id: 6, display: false, title: "受付日時", settingText: "受付日時", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "receptionDate" },
    { id: 7, display: true, title: "配達日", settingText: "配達日", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "deliveryEstimatedDate" },
    { id: 8, display: true, title: "ステータス", settingText: "ステータス", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "status" },
    { id: 9, display: true, title: "配達担当者", settingText: "配達担当者", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "staffName" },
    { id: 11, display: true, title: "配達完了時刻", settingText: "配達完了時刻", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "deliveryCompletedTime" },
    { id: 12, display: false, title: "支払方法", settingText: "支払方法", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "paymentMethod" },
    { id: 13, display: false, title: "支払金額", settingText: "支払金額", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "paymentAmount" },
    { id: 14, display: true, title: "詳細", settingText: "詳細", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "detail" },
    { id: 16, display: true, title: "キャンセル", settingText: "キャンセル", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "cancel" },
  ];

  const tableHeaders = ref<TableHeader[]>([]);

  const initializeTableHeaders = (pageKey: string): void => {
    const tableHeadersStr = localStorageGetPageHeaders(pageKey);
    if (tableHeadersStr === null) {
      tableHeaders.value = JSON.parse(JSON.stringify(defaultTableHeaders)) as TableHeader[];
      return;
    }
    tableHeaders.value = JSON.parse(tableHeadersStr) as TableHeader[];
  };

  const filteredTableHeaders = (): TableHeader[] =>
    tableHeaders.value.filter((value) => value.display);

  return {
    defaultTableHeaders,
    tableHeaders,
    initializeTableHeaders,
    filteredTableHeaders,
  };
};

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliveryRequestTableSettingsParams {
  pageKey: string;
  defaultTableHeaders: TableHeader[];
  tableHeaders: Ref<TableHeader[]>;
  displayListSettingDialog: Ref<boolean>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestTableSettings = ({
  pageKey,
  defaultTableHeaders,
  tableHeaders,
  displayListSettingDialog,
}: UseDeliveryRequestTableSettingsParams) => {
  const tableHeadersSetting = ref<TableHeader[]>([]);

  const onClickListSetting = (): void => {
    tableHeadersSetting.value = JSON.parse(
      JSON.stringify(tableHeaders.value)
    ) as TableHeader[];
    displayListSettingDialog.value = true;
  };

  const onClickListSettingCancel = (): void => {
    tableHeadersSetting.value = [];
    displayListSettingDialog.value = false;
  };

  const onClickListSettingReset = (): void => {
    tableHeadersSetting.value = JSON.parse(
      JSON.stringify(defaultTableHeaders)
    ) as TableHeader[];
  };

  const onClickListSettingOk = (): void => {
    tableHeaders.value = tableHeadersSetting.value;
    localStorageSetPageHeaders(pageKey, JSON.stringify(tableHeadersSetting.value));
    tableHeadersSetting.value = [];
    displayListSettingDialog.value = false;
  };

  return {
    tableHeadersSetting,
    onClickListSetting,
    onClickListSettingCancel,
    onClickListSettingReset,
    onClickListSettingOk,
  };
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestTable = ({
  formatDateForDisplay,
  searchConditionReceptionNo,
  searchConditionMemberId,
  searchConditionName,
  searchConditionPhone,
  searchConditionZipCode,
  searchConditionRegisterNo,
  searchConditionReceiptNo,
  searchConditionReceptionDateFrom,
  searchConditionReceptionDateTo,
  searchConditionDeliveryDateFrom,
  searchConditionDeliveryDateTo,
  areaSelectValues,
  statusSelectedItem,
  searchConditionChainCode,
  searchConditionShopCode,
  authType,
  chainViewFlag,
  isDeliveryAddressDisplay,
  isPersonalInfoPossible,
  errdialog,
  errMessage,
  editPage,
  displayListSearchDialog,
}: UseDeliveryRequestTableParams) => {
  const listLoading = ref(false);
  const listOptions = ref({
    page: 1,
    itemsPerPage: 10,
    sortBy: [] as string[],
    sortDesc: [] as boolean[],
    groupBy: [] as string[],
    groupDesc: [] as boolean[],
    multiSort: false,
    mustSort: false,
  });
  const listDataArray = ref<any[]>([]);
  const listDataTotalCount = ref(0);
  const displayPagingRangeText = ref("");
  const searchShopSelectValues = ref<string[]>([]);
  const isSearchButtonClick = ref(false);

  const pageCount = computed(() =>
    Math.max(1, Math.ceil(listDataTotalCount.value / (listOptions.value.itemsPerPage || 1)))
  );

  const searchQuery = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();

    const chainInfo = await getChainInfo(
      localStorageGetCustomerCode(),
      localStorageGetChainCode()
    );
    if (chainInfo !== undefined && chainInfo !== null) {
      isDeliveryAddressDisplay.value = chainInfo.is_delivery_address1_display ?? 0;
    }

    const listInput: DeliveryRequestListInput = {
      reception_no:
        searchConditionReceptionNo.value !== undefined &&
        searchConditionReceptionNo.value !== null &&
        searchConditionReceptionNo.value !== ""
          ? searchConditionReceptionNo.value
          : undefined,
      member_id:
        searchConditionMemberId.value !== undefined &&
        searchConditionMemberId.value !== null &&
        searchConditionMemberId.value !== ""
          ? Number(searchConditionMemberId.value)
          : undefined,
      name:
        searchConditionName.value !== undefined &&
        searchConditionName.value !== null &&
        searchConditionName.value !== ""
          ? searchConditionName.value
          : undefined,
      phone:
        searchConditionPhone.value !== undefined &&
        searchConditionPhone.value !== null &&
        searchConditionPhone.value !== ""
          ? searchConditionPhone.value
          : undefined,
      zip_code:
        searchConditionZipCode.value !== undefined &&
        searchConditionZipCode.value !== null &&
        searchConditionZipCode.value !== ""
          ? searchConditionZipCode.value
          : undefined,
      register_no:
        searchConditionRegisterNo.value !== undefined &&
        searchConditionRegisterNo.value !== null &&
        searchConditionRegisterNo.value > 0
          ? searchConditionRegisterNo.value
          : undefined,
      receipt_no:
        searchConditionReceiptNo.value !== undefined &&
        searchConditionReceiptNo.value !== null &&
        searchConditionReceiptNo.value > 0
          ? searchConditionReceiptNo.value
          : undefined,
      reception_date_from:
        searchConditionReceptionDateFrom.value !== null
          ? formatDateForDisplay(searchConditionReceptionDateFrom.value)
          : undefined,
      reception_date_to:
        searchConditionReceptionDateTo.value !== null
          ? formatDateForDisplay(searchConditionReceptionDateTo.value)
          : undefined,
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== null
          ? formatDateForDisplay(searchConditionDeliveryDateFrom.value)
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== null
          ? formatDateForDisplay(searchConditionDeliveryDateTo.value)
          : undefined,
      area_code:
        areaSelectValues.value !== undefined &&
        areaSelectValues.value !== null &&
        areaSelectValues.value.area_code !== ""
          ? areaSelectValues.value.area_code
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value.map((status) => String(status))
          : undefined,
      chain_code_list:
        authType.value === "0" || authType.value === "1"
          ? [localStorageGetChainCode() as string]
          : chainViewFlag
            ? (searchConditionChainCode.value as string[])
            : [localStorageGetChainCode() as string],
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      shop_code_list:
        authType.value === "0"
          ? [localStorageGetShopCode() as string]
          : (searchConditionShopCode.value as string[]),
      group: localStorageGetGroup(),
      limit: listOptions.value.itemsPerPage,
      offset: (listOptions.value.page - 1) * listOptions.value.itemsPerPage,
      is_delivery_address1_display: isDeliveryAddressDisplay.value,
    };

    const operationInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    const countInput: DeliveryRequestCountInput = {
      reception_no: listInput.reception_no,
      member_id: listInput.member_id,
      name: listInput.name,
      phone: listInput.phone,
      zip_code: listInput.zip_code,
      register_no: listInput.register_no,
      receipt_no: listInput.receipt_no,
      reception_date_from: listInput.reception_date_from,
      reception_date_to: listInput.reception_date_to,
      delivery_date_from: listInput.delivery_date_from,
      delivery_date_to: listInput.delivery_date_to,
      area_code: listInput.area_code,
      delivery_status: listInput.delivery_status,
      chain_code: listInput.chain_code,
      chain_code_list: listInput.chain_code_list,
      customer_code: listInput.customer_code,
      user_id: localStorageGetUserId(),
      shop_code_list: listInput.shop_code_list,
    };

    let result: ApolloQueryResult<ListDeliveryRequestQuery>;
    try {
      result = await apolloClient.query<
        ListDeliveryRequestQuery,
        ListDeliveryRequestQueryVariables
      >({
        query: ListDeliveryRequestDocument,
        variables: {
          listInput,
          logInput: operationInput,
          countInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value =
        err instanceof Error
          ? err.message
          : "配達依頼一覧データの取得に失敗しました。";
      return;
    }

    if (
      result.data.listDeliveryRequest === undefined ||
      result.data.listDeliveryRequest === null ||
      result.data.getDeliveryRequestCount === undefined ||
      result.data.getDeliveryRequestCount === null
    ) {
      errdialog.value = true;
      errMessage.value = "配達依頼一覧データの取得に失敗しました。";
      return;
    }

    const rows: any[] = [];

    result.data.listDeliveryRequest.forEach((value) => {
      if (value === undefined || value === null) {
        return;
      }

      let startTime: string[] = [];
      let endTime: string[] = [];
      let deliveryId = "";
      let deliveryIdName = "";
      let deliveryTime = "";
      let deliveryAddress = "";

      if (value.delivery_start_time) {
        startTime = value.delivery_start_time.split(":");
      }
      if (value.delivery_end_time) {
        endTime = value.delivery_end_time.split(":");
      }
      if (value.irregular_delivery_start_time) {
        startTime = value.irregular_delivery_start_time.split(":");
      }
      if (value.irregular_delivery_end_time) {
        endTime = value.irregular_delivery_end_time.split(":");
      }

      if (value.delivery_id === 0) {
        deliveryId = "都度便";
        deliveryIdName = "都度便";
      } else {
        deliveryId = `${value.delivery_id}便`;
        deliveryIdName = `${value.delivery_id}便`;
      }

      if (startTime.length > 0 && endTime.length > 0) {
        deliveryTime =
          `${startTime[0].padStart(2, "0")}:${startTime[1].padStart(2, "0")}` +
          `〜${endTime[0].padStart(2, "0")}:${endTime[1].padStart(2, "0")}`;
        if (value.delivery_id === 0) {
          deliveryIdName += `(${deliveryTime})`;
        }
      }

      if (value.delivery_zip_code) {
        deliveryAddress = `〒${value.delivery_zip_code.substring(0, 3)}-`;
        deliveryAddress += `${value.delivery_zip_code.substring(3)} `;
      }
      if (value.delivery_address1) {
        deliveryAddress += value.delivery_address1;
      }
      if (value.delivery_address2) {
        deliveryAddress += ` ${value.delivery_address2}`;
      }
      if (value.delivery_address3) {
        deliveryAddress += ` ${value.delivery_address3}`;
      }

      rows.push({
        receptionNo: value.reception_no,
        chainCode: value.chain_code,
        chainName: value.chain_name,
        shopName: value.ship_from_name,
        name: isPersonalInfoPossible.value
          ? `${value.delivery_name1} ${value.delivery_name2}`
          : GeneralConstant.word_hide,
        nameKana: isPersonalInfoPossible.value
          ? `${value.delivery_name_kana1} ${value.delivery_name_kana2}`
          : GeneralConstant.word_hide,
        areaName: "",
        receptionDate: value.created_date,
        deliveryEstimatedDate: value.planned_delivery_date
          ? value.planned_delivery_date.substring(0, 10)
          : value.required_delivery_date
            ? value.required_delivery_date.substring(0, 10)
            : "",
        deliveryEstimatedTime: value.planned_delivery_date
          ? value.planned_delivery_date.substring(11, 16)
          : "",
        deliveryIdName,
        status: pickupDeliveryStatusName(value.delivery_status),
        deliveryCompletedTime: value.completed_delivery_time!,
        staffName: value.staff_name!,
        memberId: value.member_id ? String(value.member_id) : "",
        paymentMethod: value.payment_method ? value.payment_method : "",
        unit: value.unit,
        paymentAmount: value.payment_amount
          ? String(value.payment_amount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
          : "",
        deliveryId,
        deliveryTime,
        deliveryPhone: value.delivery_phone,
        deliveryAddress,
        deliveryNotes: value.delivery_notes!,
        editAllowed:
          value.edit_allowed === undefined || value.edit_allowed === null
            ? 0
            : value.edit_allowed,
        cancelAllowed: !(
          value.cancel_allowed === undefined ||
          value.cancel_allowed === null ||
          value.cancel_allowed === 0
        ),
      });
    });

    listDataArray.value = rows;
    listDataTotalCount.value = result.data.getDeliveryRequestCount.count;
    displayPagingRangeText.value = displayPagingRange(
      listOptions.value.page - 1,
      listOptions.value.itemsPerPage,
      listDataTotalCount.value
    );

    void editPage.value;
  };

  const onClickSearch = async (): Promise<void> => {
    if (listOptions.value.page !== 1) {
      isSearchButtonClick.value = true;
      listOptions.value.page = 1;
    }

    if (searchConditionChainCode.value !== null && searchConditionChainCode.value !== undefined) {
      if (searchConditionChainCode.value.length === 0) {
        errdialog.value = true;
        errMessage.value = "チェーンが選択されていません。";
        return;
      }
      if (searchConditionShopCode.value !== null || searchConditionShopCode.value !== undefined) {
        if (
          (localStorageGetGroup() === "1" || chainViewFlag === true) &&
          authType.value !== "0" &&
          authType.value !== "2" &&
          searchConditionChainCode.value.length === 1 &&
          (searchConditionShopCode.value as string[]).length === 0
        ) {
          errdialog.value = true;
          errMessage.value = "店舗が選択されていません。";
          return;
        }
      }
    }

    await searchQuery();
    displayListSearchDialog.value = false;
  };

  watch(
    () => listOptions.value,
    async () => {
      if (isSearchButtonClick.value) {
        isSearchButtonClick.value = false;
        return;
      }
      listLoading.value = true;
      await searchQuery();
      listLoading.value = false;
    },
    { deep: true }
  );

  return {
    listLoading,
    listOptions,
    listDataArray,
    listDataTotalCount,
    displayPagingRangeText,
    searchShopSelectValues,
    pageCount,
    isSearchButtonClick,
    onClickSearch,
    searchQuery,
  };
};
